import React from 'react';
import { Tag, Tooltip, Typography, theme } from 'antd';
import type { GlobalToken } from 'antd';

const { Text } = Typography;

export interface ProxyConfig {
  protocol: string;
  host: string;
  port: number;
  login?: string;
  password?: string;
}

export interface NetworkStatus {
  ip: string;
  state: string;
}

export interface ProxyStatus {
  protocol: string;
  host: string;
  port: number;
  state: string;
}

export interface HealthServer {
  key: string;
  country?: string;
  networkStatuses?: NetworkStatus[];
  proxyStatuses?: ProxyStatus[];
}

// Status dot colours come from the theme's semantic tokens, never literals:
// green = working, amber = wobbling, red = broken, grey = unknown.
export function stateColor(state: string, token: GlobalToken): string {
  switch (state) {
    case 'HEALTHY': return token.colorSuccess;
    case 'DEGRADED':
    case 'SUSPECT':
    case 'ZOMBIE': return token.colorWarning;
    case 'ERROR':
    case 'DOWN': return token.colorError;
    default: return token.colorTextQuaternary;
  }
}

export const stateLabelMap: Record<string, string> = {
  HEALTHY: 'Healthy',
  DEGRADED: 'Degraded',
  SUSPECT: 'Suspect',
  ZOMBIE: 'Zombie',
  ERROR: 'Error',
  DOWN: 'Down',
  UNKNOWN: 'Unknown',
};

// Higher = worse; drives the "bad-first" ordering inside the cell so that a
// long list still surfaces the most problematic entries in the visible slice.
export const STATE_SEVERITY: Record<string, number> = {
  DOWN: 7, ERROR: 6, ZOMBIE: 5, DEGRADED: 4, SUSPECT: 3, UNKNOWN: 2, HEALTHY: 1,
};

export const StatusDot: React.FC<{ state: string }> = ({ state }) => {
  const { token } = theme.useToken();
  const color = stateColor(state, token);
  const label = stateLabelMap[state] || state;
  return (
    <Tooltip title={label}>
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          marginRight: 4,
          verticalAlign: 'middle',
        }}
      />
    </Tooltip>
  );
};

// One entry of a list cell. `text` is the compact form that fits the column,
// `full` the value it stands for — the two differ only where the compact form
// drops something the row already shows (a proxy host equal to the main IP).
export interface CellItem {
  id: string;
  text: string;
  full: string;
  state: string;
}

// A chip never wraps and never grows: a server with four networks has to be the
// same height as one with none, or four rows of data cost a screen.
const chipStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  lineHeight: '18px',
  padding: '0 5px',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const stateText = (state: string) => (state ? stateLabelMap[state] || state : 'Not checked yet');

const HiddenList: React.FC<{ items: CellItem[] }> = ({ items }) => (
  <div>
    {items.map((i) => (
      <div key={i.id} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        {i.full} — {stateText(i.state)}
      </div>
    ))}
  </div>
);

// ItemsCell renders the first `max` entries inline and folds the rest into a
// "+N" chip whose tooltip spells every one of them out — nothing the old
// multi-line cell showed is unreachable, it just no longer costs three rows.
export const ItemsCell: React.FC<{ items: CellItem[]; max?: number; color?: string }> = ({
  items, max = 2, color,
}) => {
  if (items.length === 0) return <Text type="secondary" style={{ fontSize: 12 }}>--</Text>;
  const visible = items.slice(0, max);
  const hidden = items.slice(max);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      {visible.map((i) => (
        <Tooltip key={i.id} title={`${i.full} — ${stateText(i.state)}`}>
          <Tag color={color} style={chipStyle}>
            {i.state && <StatusDot state={i.state} />}
            {i.text}
          </Tag>
        </Tooltip>
      ))}
      {hidden.length > 0 && (
        <Tooltip title={<HiddenList items={hidden} />}>
          <Tag color={color} style={{ ...chipStyle, flex: '0 0 auto' }}>+{hidden.length}</Tag>
        </Tooltip>
      )}
    </div>
  );
};

// networkItems merges checked and unchecked networks, worst first. The main IP
// is left out: it has its own column on the row, with its own status dot, and
// repeating it here bought nothing but width.
export function networkItems(
  networks: string[] | undefined,
  health: HealthServer | undefined,
  mainIP: string,
): CellItem[] {
  const statuses = health?.networkStatuses || [];
  const byIP = new Map(statuses.map((s) => [s.ip, s.state]));
  const all = [
    ...statuses.map((s) => ({ ip: s.ip, state: s.state })),
    ...(networks || []).filter((n) => !byIP.has(n)).map((n) => ({ ip: n, state: '' })),
  ].filter((i) => i.ip !== mainIP);
  all.sort((a, b) => (STATE_SEVERITY[b.state] || 0) - (STATE_SEVERITY[a.state] || 0));
  return all.map((i) => ({ id: i.ip, text: i.ip, full: i.ip, state: i.state }));
}

// proxyItems does the same for proxies. Duplicate host:port entries are
// tolerated by the domain, so the react key carries the index too.
export function proxyItems(
  proxies: ProxyConfig[] | undefined,
  health: HealthServer | undefined,
  mainIP: string,
): CellItem[] {
  const statuses = health?.proxyStatuses || [];
  const merged = (proxies || []).map((p, idx) => {
    const status = statuses.find((s) => s.host === p.host && s.port === p.port);
    const full = `${p.protocol}://${p.host}:${p.port}`;
    return {
      id: `${p.host}:${p.port}#${idx}`,
      // A proxy on the server's own address repeats an IP the row already
      // shows; the port is the part that identifies the endpoint.
      text: p.host === mainIP ? `${p.protocol}:${p.port}` : full,
      full,
      state: status?.state || '',
    };
  });
  merged.sort((a, b) => (STATE_SEVERITY[b.state] || 0) - (STATE_SEVERITY[a.state] || 0));
  return merged;
}

const DetailGroup: React.FC<{ label: string; items: CellItem[]; color?: string }> = ({
  label, items, color,
}) => (
  <div style={{ minWidth: 0 }}>
    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
      {label} ({items.length})
    </Text>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.length === 0 && <Text type="secondary" style={{ fontSize: 12 }}>--</Text>}
      {items.map((i) => (
        <Tooltip key={i.id} title={stateText(i.state)}>
          <Tag color={color} style={chipStyle}>
            {i.state && <StatusDot state={i.state} />}
            {i.full}
          </Tag>
        </Tooltip>
      ))}
    </div>
  </div>
);

// ServerRowDetail is where the list cells send what they could not fit: every
// network, every proxy and every tag of one server, in full.
export const ServerRowDetail: React.FC<{
  networks: CellItem[];
  proxies: CellItem[];
  tags: string[];
}> = ({ networks, proxies, tags }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, padding: '2px 0' }}>
    <DetailGroup label="Networks" items={networks} />
    <DetailGroup label="Proxies" items={proxies} />
    {tags.length > 0 && (
      <div>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
          Tags ({tags.length})
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {tags.map((t) => <Tag key={t} style={chipStyle}>{t}</Tag>)}
        </div>
      </div>
    )}
  </div>
);
